import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  LeadsReportsTabs,
  LeadsReportsTabKey,
  ReportTypeDropdown,
} from "./common";
import { ReportsTab } from "./tabs/reports/ReportsTab";
import { LeadsTab } from "./tabs/leads/LeadsTab";
import { useStoryboardQuery } from "src/query/experiences/storyboardSettings/websiteSettings";
import {
  selectDateFilter,
  selectReportType,
} from "src/redux/selectors/experiences/leadsAndReports";
import {
  resetLeadsAndReports,
  setDateFilter,
  setReportType,
} from "src/redux/actions/experiences/leadsAndReports";
import {
  defaultDateFilter,
  generateReportUrl,
  parseQueryParams,
} from "src/services/experiences/leadsAndReports/dateUtils";
import { ReportType } from "src/types/experiences/leadsAndReports";
import styles from "./common/common.module.scss";

const getActiveTab = (pathname: string): LeadsReportsTabKey =>
  pathname.includes("/leads") ? "leads" : "reports";

export const LeadsAndReportsPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { storyboardId } = useParams<{
    storyboardId: string;
  }>();

  const reportType = useSelector(selectReportType);
  const dateFilter = useSelector(selectDateFilter);

  const { data: storyboard } = useStoryboardQuery(storyboardId ?? "");
  const storyboardType = storyboard?.type;
  const storyboardName = storyboard?.name;

  const activeTab = getActiveTab(location.pathname);

  const isLeadProfileRoute = location.pathname.includes("/leads/profile");

  useEffect(() => {
    dispatch(setDateFilter(parseQueryParams(location.search)));
    return () => {
      dispatch(resetLeadsAndReports());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const current = new URLSearchParams(location.search);

    // The lead profile route has no date filter — strip date params if present.
    if (isLeadProfileRoute) {
      if (!current.has("type") && !current.has("startDate") && !current.has("endDate"))
        return;
      ["type", "startDate", "endDate"].forEach((k) => current.delete(k));
      const query = current.toString();
      navigate(`${location.pathname}${query ? `?${query}` : ""}`, { replace: true });
      return;
    }

    if (!dateFilter) return;
    const alreadyInSync =
      current.get("type") === dateFilter.type &&
      current.get("startDate") === dateFilter.startDate.format("DD/MM/YYYY") &&
      current.get("endDate") === dateFilter.endDate.format("DD/MM/YYYY");
    if (alreadyInSync) return;
    const updated = generateReportUrl(
      location.pathname,
      dateFilter,
      location.search,
    );
    navigate(updated, { replace: true });
  }, [dateFilter, location.pathname, isLeadProfileRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (tab: LeadsReportsTabKey) => {
    const base = location.pathname.replace(/\/(leads|reports)(\/.*)?$/, "");
    const resetDate = defaultDateFilter();
    dispatch(setDateFilter(resetDate));
    dispatch(setReportType("ALL"));
    navigate(generateReportUrl(`${base}/${tab}`, resetDate, location.search));
  };

  const handleReportType = (value: ReportType) =>
    dispatch(setReportType(value));

  return (
    <div className={styles.page}>
      {!isLeadProfileRoute && (
        <div className={styles.header}>
          <LeadsReportsTabs active={activeTab} onChange={handleTabChange} />
          {activeTab === "reports" && (
            <ReportTypeDropdown value={reportType} onChange={handleReportType} />
          )}
        </div>
      )}

      {activeTab === "reports" ? (
        <ReportsTab
          storyboardId={storyboardId ?? ""}
          storyboardType={storyboardType}
        />
      ) : (
        <LeadsTab
          storyboardId={storyboardId ?? ""}
          storyboardName={storyboardName}
          storyboardType={storyboardType}
        />
      )}
    </div>
  );
};

export default LeadsAndReportsPage;
